import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIsMobile } from '../lib/media';
import { useBackEntry } from '../nav/useBackEntry';

/*
 * Всплывающее у контрола (дизайн-система 3.26): ловушка клика мимо
 * `.menu-back` и само меню `.menu.pop`.
 *
 * Почему `position:fixed` с вычисленной геометрией, а не `absolute` внутри
 * поля: у окна прокручивается тело (`.dialog-body{overflow-y:auto}`), и
 * абсолютно позиционированное меню обрезалось бы его краем. Инлайном
 * задаются только left/top/min-width — вычисляемая геометрия, как у блока
 * встречи в сетке.
 *
 * На телефоне (≤900) меню становится шторкой снизу (`.menu.sheet`,
 * дизайн-система 3.11) — геометрия целиком из CSS, инлайновой нет.
 *
 * Прокрутка страницы или окна под открытым меню его закрывает: меню
 * закреплено за окном, а не за полем, и уехало бы от своего поля. Прокрутка
 * внутри самого меню (длинный список времени) не в счёт.
 *
 * Шторка на телефоне перехватывает системную «назад» (useBackEntry): своей
 * записи в истории у неё не было, и «назад» закрывала экран под шторкой —
 * форму со всем набранным, а не саму шторку. Дизайн-система 3.11 требует от
 * Menu именно этого: «закрытие клик вне, Esc, выбор, системная „назад“».
 * Полноценным экраном стопки шторка при этом не становится: слои чата и общий
 * механизм стопки не трогаем, добавлена только запись в истории.
 */
export function Popover({
  anchor,
  onClose,
  className,
  role,
  fitAnchor = true,
  children,
}: {
  anchor: HTMLElement | null;
  onClose: () => void;
  className?: string;
  role?: string;
  /** Растягивать по ширине кнопки — так ведут себя списки; выбору даты нужна своя ширина. */
  fitAnchor?: boolean;
  children: ReactNode;
}) {
  const mobile = useIsMobile();
  const box = useRef<HTMLDivElement>(null);
  const [geo, setGeo] = useState<{ left: number; top: number; width: number } | null>(null);

  // Компонент живёт только пока всплывающее открыто, поэтому запись в истории
  // нужна всё это время. На десктопе всплывающее закрывают Esc и клик мимо —
  // записи там нет, чтобы не удлинять историю у обычного выпадающего списка.
  //
  // Запись ставится со второго кадра, а не сразу при монтировании: React в
  // разработке (StrictMode) прогоняет эффекты дважды — push, снятие, снова
  // push, — а снятие верхней записи откатывает историю (controller.remove →
  // history.back). Пришедший следом popstate снял бы нашу же новую запись и
  // закрыл шторку в тот же миг, как она открылась. Через состояние active у
  // useBackEntry идёт false → true, то есть обычная смена, которую StrictMode
  // не удваивает. Общий механизм стопки при этом не тронут.
  const [armed, setArmed] = useState(false);
  useEffect(() => setArmed(true), []);
  useBackEntry(mobile && armed, onClose);

  useLayoutEffect(() => {
    if (mobile || !anchor) {
      setGeo(null);
      return;
    }
    const r = anchor.getBoundingClientRect();
    const h = box.current?.offsetHeight ?? 0;
    // ширина меню: своя или кнопки, если меню тянется по ней
    const w = Math.max(box.current?.offsetWidth ?? 0, fitAnchor ? r.width : 0);
    // не помещается снизу, но помещается сверху — раскрываем вверх
    const up = h > 0 && window.innerHeight - r.bottom < h + 8 && r.top > h + 8;
    // не помещается справа — встаёт левее, к краю окна с полем 8: кнопка
    // «Ещё действия» стоит у правого края строки (дизайн-система 3.11, версия 3.03)
    const left = w > 0 && r.left + w > window.innerWidth - 8 ? Math.max(8, window.innerWidth - w - 8) : r.left;
    setGeo({ left, top: up ? r.top - h - 4 : r.bottom + 4, width: r.width });
  }, [anchor, mobile, fitAnchor]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    const onScroll = (e: Event) => {
      if (box.current?.contains(e.target as Node)) return;
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onClose);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  return (
    <>
      <div className="menu-back" onClick={onClose} />
      <div
        ref={box}
        role={role}
        className={`menu pop${mobile ? ' sheet' : ''}${className ? ` ${className}` : ''}`}
        style={geo ? { left: geo.left, top: geo.top, ...(fitAnchor ? { minWidth: geo.width } : {}) } : undefined}
      >
        {children}
      </div>
    </>
  );
}
